import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTemplatesList } from "../../../api/login.service";
import LoadingComponent from '../../../components/LoadingComponent';

interface FlowStep {
  id: string;
  templateId: string;
  templateName: string;
  language: string;
  order: number;
  conditions: FlowCondition[];
  delay?: number; // delay in minutes before next step
  isActive: boolean;
  
  // LLM Function Configuration
  llmConfig: {
    systemPrompt: string;
    functionParameters: {
      inputFields: string[];
      outputFormat: string;
      requiredData: string[];
      optionalData: string[];
    };
    modelSettings: {
      model: string; // 'gpt-4', 'gpt-3.5-turbo', 'claude', etc.
      temperature: number; // 0.0 to 1.0
      maxTokens: number;
      topP: number;
    };
    businessContext: {
      industry: string;
      businessRules: string[];
      customerProfile: string;
      toneOfVoice: string;
    };
  };
}

interface FlowCondition {
  id: string;
  type: 'customer_action' | 'parameter_match' | 'time_based' | 'ai_decision';
  field: string;
  operator: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'exists' | 'ai_suggested';
  value: string;
  description: string;
}

interface AutomatedFlow {
  id: string;
  name: string;
  description: string;
  trigger: 'appointment_created' | 'customer_message' | 'parameter_match' | 'ai_detected' | 'manual';
  triggerConditions: FlowCondition[];
  steps: FlowStep[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  totalConversations: number;
  successRate: number;
  languages: string[];
  useCase?: string; // Added for edit form
}

interface FlowTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  status: string;
  content: string;
  variables: string[];
}

interface FlowConversation {
  id: string;
  flowId: string;
  customerPhone: string;
  currentStep: number;
  status: 'active' | 'completed' | 'failed' | 'paused';
  variables: Record<string, string>;
  lastMessageAt: string;
  nextStepAt?: string;
  aiSuggestions: string[];
}

export default function AutomatedFlowsManager() {
  const { wabaId } = useParams<{ wabaId: string }>();
  const navigate = useNavigate();
  
  // State for flows
  const [flows, setFlows] = useState<AutomatedFlow[]>([]);
  const [selectedFlow, setSelectedFlow] = useState<AutomatedFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for flow operations
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFlowBuilder, setShowFlowBuilder] = useState(false);
  const [showConversationView, setShowConversationView] = useState(false);
  const [showStepEditor, setShowStepEditor] = useState(false);
  
  // Form states
  const [flowForm, setFlowForm] = useState({
    name: '',
    description: '',
    useCase: '',
    trigger: 'appointment_created' as 'appointment_created' | 'customer_message' | 'parameter_match' | 'ai_detected' | 'manual',
    languages: ['en'] as string[]
  });
  
  // Flow builder states
  const [currentStep, setCurrentStep] = useState<FlowStep | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<FlowTemplate[]>([]);
  const [flowSteps, setFlowSteps] = useState<FlowStep[]>([]);
  
  // Step editor states
  const [stepForm, setStepForm] = useState({
    templateId: '',
    templateName: '',
    language: 'en',
    delay: 0,
    systemPrompt: '',
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    maxTokens: 150,
    industry: 'Restaurant',
    toneOfVoice: 'Friendly and professional',
    requiredData: [] as string[],
    optionalData: [] as string[]
  });
  
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterTrigger, setFilterTrigger] = useState<string>('all');
  const [filterUseCase, setFilterUseCase] = useState<string>('all');

  // Fetch automated flows from API
  const fetchAutomatedFlows = async () => {
    setLoading(true);
    try {
      // For now, we'll use mock data since the API endpoint might not exist yet
      // In production, this would be: fetch(`https://api.frontdesk24.com/api/automated-flows/${wabaId}`)
      
      const mockFlows: AutomatedFlow[] = [
        {
          id: 'flow_1',
          name: 'Restaurant Table Booking Flow',
          description: 'Automated flow for table reservations and confirmations',
          useCase: 'table_booking',
          trigger: 'appointment_created',
          triggerConditions: [
            {
              id: 'cond_1',
              type: 'customer_action',
              field: 'action_type',
              operator: 'equals',
              value: 'table_booking_created',
              description: 'When customer books a table'
            }
          ],
          steps: [
            {
              id: 'step_1',
              templateId: 'template_1',
              templateName: 'Table Booking Confirmation',
              language: 'en',
              order: 1,
              conditions: [],
              delay: 0,
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to confirm the customer's table booking.",
                functionParameters: {
                  inputFields: ['booking_time', 'customer_name', 'number_of_guests', 'special_requests'],
                  outputFormat: 'JSON',
                  requiredData: ['booking_time', 'customer_name'],
                  optionalData: ['number_of_guests', 'special_requests']
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.7,
                  maxTokens: 150,
                  topP: 0.9
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Always be polite and professional.', 'Never disclose personal information.'],
                  customerProfile: 'Customer is booking a table for a group of 4.',
                  toneOfVoice: 'Friendly and informative.'
                }
              }
            },
            {
              id: 'step_2',
              templateId: 'template_2',
              templateName: 'Reminder 2h Before',
              language: 'en',
              order: 2,
              conditions: [
                {
                  id: 'cond_2',
                  type: 'time_based',
                  field: 'booking_time',
                  operator: 'greater_than',
                  value: '2h',
                  description: '2 hours before reservation'
                }
              ],
              delay: 120, // 2 hours
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to send a reminder to the customer 2 hours before their table booking.",
                functionParameters: {
                  inputFields: ['booking_time', 'customer_name'],
                  outputFormat: 'JSON',
                  requiredData: ['booking_time', 'customer_name'],
                  optionalData: []
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.5,
                  maxTokens: 100,
                  topP: 0.8
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Be concise and direct.', 'Do not include personal opinions.'],
                  customerProfile: 'Customer is a regular customer.',
                  toneOfVoice: 'Professional and polite.'
                }
              }
            },
            {
              id: 'step_3',
              templateId: 'template_3',
              templateName: 'Thank You After Visit',
              language: 'en',
              order: 3,
              conditions: [
                {
                  id: 'cond_3',
                  type: 'parameter_match',
                  field: 'visit_status',
                  operator: 'equals',
                  value: 'completed',
                  description: 'After customer visits'
                }
              ],
              delay: 60, // 1 hour
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to thank the customer for their visit and ask for feedback.",
                functionParameters: {
                  inputFields: ['customer_name'],
                  outputFormat: 'JSON',
                  requiredData: ['customer_name'],
                  optionalData: []
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.6,
                  maxTokens: 120,
                  topP: 0.9
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Be genuine and appreciative.', 'Do not ask for personal information.'],
                  customerProfile: 'Customer is a regular customer.',
                  toneOfVoice: 'Friendly and conversational.'
                }
              }
            }
          ],
          isActive: true,
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
          updatedAt: new Date().toISOString(),
          totalConversations: 45,
          successRate: 92.5,
          languages: ['en', 'es']
        },
        {
          id: 'flow_2',
          name: 'Food Ordering & Delivery Flow',
          description: 'Complete food ordering process from order to delivery',
          useCase: 'food_ordering',
          trigger: 'customer_message',
          triggerConditions: [
            {
              id: 'cond_4',
              type: 'customer_action',
              field: 'message_type',
              operator: 'contains',
              value: 'order food',
              description: 'When customer wants to order food'
            }
          ],
          steps: [
            {
              id: 'step_4',
              templateId: 'template_4',
              templateName: 'Order Confirmation',
              language: 'en',
              order: 1,
              conditions: [],
              delay: 0,
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to confirm the customer's food order.",
                functionParameters: {
                  inputFields: ['order_items', 'customer_name', 'delivery_address'],
                  outputFormat: 'JSON',
                  requiredData: ['order_items', 'customer_name'],
                  optionalData: ['delivery_address']
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.7,
                  maxTokens: 150,
                  topP: 0.9
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Always be polite and professional.', 'Never disclose personal information.'],
                  customerProfile: 'Customer is ordering delivery for 2 burgers and a soda.',
                  toneOfVoice: 'Friendly and informative.'
                }
              }
            },
            {
              id: 'step_5',
              templateId: 'template_5',
              templateName: 'Kitchen Update',
              language: 'en',
              order: 2,
              conditions: [
                {
                  id: 'cond_5',
                  type: 'parameter_match',
                  field: 'order_status',
                  operator: 'equals',
                  value: 'preparing',
                  description: 'When food is being prepared'
                }
              ],
              delay: 15,
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to update the customer on the status of their food order.",
                functionParameters: {
                  inputFields: ['order_id', 'status'],
                  outputFormat: 'JSON',
                  requiredData: ['order_id', 'status'],
                  optionalData: []
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.5,
                  maxTokens: 100,
                  topP: 0.8
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Be concise and direct.', 'Do not include personal opinions.'],
                  customerProfile: 'Customer is a regular customer.',
                  toneOfVoice: 'Professional and polite.'
                }
              }
            },
            {
              id: 'step_6',
              templateId: 'template_6',
              templateName: 'Delivery Status',
              language: 'en',
              order: 3,
              conditions: [
                {
                  id: 'cond_6',
                  type: 'parameter_match',
                  field: 'order_status',
                  operator: 'equals',
                  value: 'out_for_delivery',
                  description: 'When food is out for delivery'
                }
              ],
              delay: 0,
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to inform the customer about the delivery status of their food order.",
                functionParameters: {
                  inputFields: ['order_id', 'status'],
                  outputFormat: 'JSON',
                  requiredData: ['order_id', 'status'],
                  optionalData: []
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.6,
                  maxTokens: 120,
                  topP: 0.9
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Be genuine and appreciative.', 'Do not ask for personal information.'],
                  customerProfile: 'Customer is a regular customer.',
                  toneOfVoice: 'Friendly and conversational.'
                }
              }
            }
          ],
          isActive: true,
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
          updatedAt: new Date().toISOString(),
          totalConversations: 128,
          successRate: 87.3,
          languages: ['en']
        },
        {
          id: 'flow_3',
          name: 'Food & Menu Inquiry Flow',
          description: 'AI-powered responses to food and menu questions',
          useCase: 'food_inquiry',
          trigger: 'customer_message',
          triggerConditions: [
            {
              id: 'cond_7',
              type: 'customer_action',
              field: 'message_type',
              operator: 'contains',
              value: 'menu',
              description: 'When customer asks about menu'
            }
          ],
          steps: [
            {
              id: 'step_7',
              templateId: 'template_7',
              templateName: 'Menu Information',
              language: 'en',
              order: 1,
              conditions: [],
              delay: 0,
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to provide detailed information about the restaurant's menu.",
                functionParameters: {
                  inputFields: ['menu_items'],
                  outputFormat: 'JSON',
                  requiredData: ['menu_items'],
                  optionalData: []
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.7,
                  maxTokens: 200,
                  topP: 0.9
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Always be polite and professional.', 'Never disclose personal information.'],
                  customerProfile: 'Customer is a regular customer.',
                  toneOfVoice: 'Friendly and informative.'
                }
              }
            },
            {
              id: 'step_8',
              templateId: 'template_8',
              templateName: 'AI Food Recommendations',
              language: 'en',
              order: 2,
              conditions: [
                {
                  id: 'cond_8',
                  type: 'ai_decision',
                  field: 'ai_suggestion',
                  operator: 'ai_suggested',
                  value: 'recommendations',
                  description: 'AI suggests best dishes'
                }
              ],
              delay: 2,
              isActive: true,
              llmConfig: {
                systemPrompt: "You are a restaurant assistant. Your task is to provide AI-powered food recommendations based on the customer's preferences.",
                functionParameters: {
                  inputFields: ['customer_preferences'],
                  outputFormat: 'JSON',
                  requiredData: ['customer_preferences'],
                  optionalData: []
                },
                modelSettings: {
                  model: 'gpt-3.5-turbo',
                  temperature: 0.6,
                  maxTokens: 150,
                  topP: 0.9
                },
                businessContext: {
                  industry: 'Restaurant',
                  businessRules: ['Always be polite and professional.', 'Never disclose personal information.'],
                  customerProfile: 'Customer is a regular customer.',
                  toneOfVoice: 'Friendly and informative.'
                }
              }
            }
          ],
          isActive: true,
          createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
          updatedAt: new Date().toISOString(),
          totalConversations: 89,
          successRate: 94.2,
          languages: ['en', 'es', 'fr']
        }
      ];
      
      setFlows(mockFlows);
      
    } catch (err: any) {
      setError(err?.message || 'Failed to load automated flows');
    } finally {
      setLoading(false);
    }
  };

  // Fetch available templates for flow building
  const fetchAvailableTemplates = async () => {
    try {
      const data = await getTemplatesList(wabaId);
      const templates: FlowTemplate[] = (data.data || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        category: t.category,
        language: t.language,
        status: t.status,
        content: t.components?.find((c: any) => c.type === 'BODY')?.text || t.content || '',
        variables: (t.components?.find((c: any) => c.type === 'BODY')?.text || '')
          .match(/\{\{([^}]+)\}\}/g)?.map((v: string) => v.replace(/\{\{|\}\}/g, '')) || []
      }));
      
      setAvailableTemplates(templates);
    } catch (err: any) {
      console.error('Failed to fetch templates:', err);
    }
  };

  useEffect(() => {
    if (wabaId) {
      fetchAutomatedFlows();
      fetchAvailableTemplates();
    }
  }, [wabaId]);

  // Create new flow
  const handleCreateFlow = async () => {
    if (!flowForm.name.trim()) {
      setError('Flow name is required');
      return;
    }

    try {
      const newFlow: AutomatedFlow = {
        id: `flow_${Date.now()}`,
        name: flowForm.name.trim(),
        description: flowForm.description.trim(),
        trigger: flowForm.trigger,
        triggerConditions: [],
        steps: [],
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        totalConversations: 0,
        successRate: 0,
        languages: flowForm.languages
      };

      setFlows(prev => [...prev, newFlow]);
      setFlowForm({ name: '', description: '', useCase: '', trigger: 'appointment_created', languages: ['en'] });
      setShowCreateForm(false);
      setError(null);
      
      // Open flow builder for the new flow
      setSelectedFlow(newFlow);
      setShowFlowBuilder(true);
      
    } catch (err: any) {
      setError(err?.message || 'Failed to create flow');
    }
  };

  // Edit flow
  const handleEditFlow = async () => {
    if (!selectedFlow || !flowForm.name.trim()) {
      setError('Flow name is required');
      return;
    }

    try {
      const updatedFlow: AutomatedFlow = {
        ...selectedFlow,
        name: flowForm.name.trim(),
        description: flowForm.description.trim(),
        trigger: flowForm.trigger,
        languages: flowForm.languages,
        updatedAt: new Date().toISOString()
      };

      setFlows(prev => prev.map(f => f.id === selectedFlow.id ? updatedFlow : f));
      setSelectedFlow(null);
      setFlowForm({ name: '', description: '', useCase: '', trigger: 'appointment_created', languages: ['en'] });
      setShowEditForm(false);
      setError(null);
      
    } catch (err: any) {
      setError(err?.message || 'Failed to update flow');
    }
  };

  // Delete flow
  const handleDeleteFlow = async () => {
    if (!selectedFlow) return;

    try {
      setFlows(prev => prev.filter(f => f.id !== selectedFlow.id));
      setSelectedFlow(null);
      setShowDeleteConfirm(false);
      setError(null);
      
    } catch (err: any) {
      setError(err?.message || 'Failed to delete flow');
    }
  };

  // Add step to flow
  const handleAddStep = () => {
    if (!selectedFlow) return;

    const newStep: FlowStep = {
      id: `step_${Date.now()}`,
      templateId: '',
      templateName: '',
      language: 'en',
      order: selectedFlow.steps.length + 1,
      conditions: [],
      delay: 0,
      isActive: true,
      llmConfig: {
        systemPrompt: "You are a restaurant assistant. Your task is to confirm the customer's table booking.",
        functionParameters: {
          inputFields: ['booking_time', 'customer_name', 'number_of_guests', 'special_requests'],
          outputFormat: 'JSON',
          requiredData: ['booking_time', 'customer_name'],
          optionalData: ['number_of_guests', 'special_requests']
        },
        modelSettings: {
          model: 'gpt-3.5-turbo',
          temperature: 0.7,
          maxTokens: 150,
          topP: 0.9
        },
        businessContext: {
          industry: 'Restaurant',
          businessRules: ['Always be polite and professional.', 'Never disclose personal information.'],
          customerProfile: 'Customer is booking a table for a group of 4.',
          toneOfVoice: 'Friendly and informative.'
        }
      }
    };

    setFlowSteps(prev => [...prev, newStep]);
    setCurrentStep(newStep);
  };

  // Save flow steps
  const handleSaveFlowSteps = () => {
    if (!selectedFlow) return;

    const updatedFlow: AutomatedFlow = {
      ...selectedFlow,
      steps: flowSteps,
      updatedAt: new Date().toISOString()
    };

    setFlows(prev => prev.map(f => f.id === selectedFlow.id ? updatedFlow : f));
    setShowFlowBuilder(false);
    setSelectedFlow(null);
    setFlowSteps([]);
    setCurrentStep(null);
  };

  // Filter flows based on search and filters
  const filteredFlows = flows.filter(flow => {
    const matchesSearch = flow.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         flow.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && flow.isActive) ||
                         (filterStatus === 'inactive' && !flow.isActive);
    const matchesTrigger = filterTrigger === 'all' || flow.trigger === filterTrigger;
    const matchesUseCase = filterUseCase === 'all' || flow.useCase === filterUseCase;
    
    return matchesSearch && matchesStatus && matchesTrigger && matchesUseCase;
  });

  // Open edit form
  const openEditForm = (flow: AutomatedFlow) => {
    setSelectedFlow(flow);
    setFlowForm({ 
      name: flow.name, 
      description: flow.description, 
      useCase: flow.useCase || '',
      trigger: flow.trigger,
      languages: flow.languages,

    });
    setShowEditForm(true);
  };

  // Open flow builder
  const openFlowBuilder = (flow: AutomatedFlow) => {
    setSelectedFlow(flow);
    setFlowSteps([...flow.steps]);
    setShowFlowBuilder(true);
  };

  // Open delete confirmation
  const openDeleteConfirm = (flow: AutomatedFlow) => {
    setSelectedFlow(flow);
    setShowDeleteConfirm(true);
  };

  // Remove the early return for loading - let the UI render with overlay

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-7xl">
        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold">Automated Message Flows</h1>
              <p className="text-sm text-gray-500">AI-powered automated messaging with smart triggers and conditions</p>
              {wabaId && (
                <p className="text-xs text-gray-400 mt-1">WABA ID: {wabaId}</p>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCreateForm(true)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm hover:bg-blue-700"
              >
                Create Flow
              </button>
              <button
                onClick={() => navigate('/events/business-personality-settings')}
                className="rounded-lg bg-purple-600 px-4 py-2 text-white shadow-sm hover:bg-purple-700"
              >
                Business Personality Settings
              </button>
              <button
                onClick={() => navigate('/events/whatsapp-marketing-send')}
                className="rounded-lg bg-green-600 px-4 py-2 text-white shadow-sm hover:bg-green-700"
              >
                Send Marketing
              </button>
              <button
                onClick={() => navigate('/events/whatsapp-marketing-list')}
                className="rounded-lg bg-gray-600 px-4 py-2 text-white shadow-sm hover:bg-gray-700"
              >
                ← Back to Marketing
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        {/* Search and Filter */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search flows..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none md:w-80"
            />
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select
              value={filterUseCase}
              onChange={(e) => setFilterUseCase(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
            >
              <option value="all">All Use Cases</option>
              <optgroup label="Restaurant & Food">
                <option value="table_booking">Table Booking</option>
                <option value="food_ordering">Food Ordering</option>
                <option value="food_inquiry">Food Inquiry</option>
                <option value="special_offers">Special Offers</option>
                <option value="customer_feedback">Customer Feedback</option>
              </optgroup>
              <optgroup label="Healthcare">
                <option value="appointment_booking">Appointment Booking</option>
                <option value="medication_reminder">Medication Reminders</option>
                <option value="follow_up_care">Follow-up Care</option>
                <option value="health_tips">Health Tips</option>
              </optgroup>
              <optgroup label="E-commerce">
                <option value="order_tracking">Order Tracking</option>
                <option value="abandoned_cart">Abandoned Cart</option>
                <option value="product_inquiry">Product Inquiry</option>
                <option value="customer_support">Customer Support</option>
              </optgroup>
              <optgroup label="Services">
                <option value="service_booking">Service Booking</option>
                <option value="appointment_reminder">Appointment Reminders</option>
                <option value="status_updates">Status Updates</option>
                <option value="feedback_collection">Feedback Collection</option>
              </optgroup>
            </select>
            <select
              value={filterTrigger}
              onChange={(e) => setFilterTrigger(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
            >
              <option value="all">All Triggers</option>
              <option value="appointment_created">Appointment Created</option>
              <option value="customer_message">Customer Message</option>
              <option value="parameter_match">Parameter Match</option>
              <option value="ai_detected">AI Detected</option>
              <option value="manual">Manual</option>
            </select>
          </div>
        </div>

        {/* Flows Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredFlows.map((flow) => (
            <div key={flow.id} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="mb-4 flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">{flow.name}</h3>
                  <p className="mt-1 text-sm text-gray-600">{flow.description}</p>
                  <div className="mt-2 flex items-center space-x-2">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      flow.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {flow.isActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                      {flow.trigger.replace('_', ' ')}
                    </span>
                    {flow.useCase && (
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                        {flow.useCase.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="ml-3 flex space-x-2">
                  <button
                    onClick={() => openFlowBuilder(flow)}
                    className="rounded-md bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-200 transition-colors"
                  >
                    Builder
                  </button>
                  <button
                    onClick={() => openEditForm(flow)}
                    className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => openDeleteConfirm(flow)}
                    className="rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Steps:</span>
                  <span className="font-medium text-gray-900">{flow.steps.length}</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                  <div 
                    className="h-2 rounded-full bg-blue-500 transition-all duration-300"
                    style={{ width: `${Math.min((flow.steps.length / 5) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Success Rate:</span>
                  <span className="font-medium text-gray-900">{flow.successRate}%</span>
                </div>
                <div className="mt-2 h-2 w-full rounded-full bg-gray-200">
                  <div 
                    className={`h-2 rounded-full transition-all duration-300 ${
                      flow.successRate >= 90 ? 'bg-green-500' : 
                      flow.successRate >= 70 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${flow.successRate}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                <span>Created: {new Date(flow.createdAt).toLocaleDateString()}</span>
                <span>{flow.totalConversations} conversations</span>
              </div>



              {flow.languages.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-gray-500 mb-2">Languages:</div>
                  <div className="flex flex-wrap gap-1">
                    {flow.languages.map(lang => (
                      <span key={lang} className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                        {lang.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="mt-4 flex space-x-2">
                <button
                  onClick={() => openFlowBuilder(flow)}
                  className="flex-1 rounded-lg bg-purple-100 px-3 py-2 text-sm font-medium text-purple-700 hover:bg-purple-200 transition-colors"
                >
                  Build Flow
                </button>
                <button
                  onClick={() => setShowConversationView(true)}
                  className="flex-1 rounded-lg bg-green-100 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-200 transition-colors"
                >
                  View Conversations
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredFlows.length === 0 && (
          <div className="mt-8 text-center">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-8">
              <p className="text-gray-500">No automated flows found.</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
              >
                Create your first flow
              </button>
            </div>
          </div>
        )}

        {/* Create Flow Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
              <h3 className="mb-4 text-lg font-semibold">Create New Flow</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flow Name *</label>
                  <input
                    type="text"
                    value={flowForm.name}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter flow name"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={flowForm.description}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter flow description"
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Business Use Case</label>
                  <select
                    value={flowForm.useCase || ''}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, useCase: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="">Select a business use case</option>
                    <optgroup label="Restaurant & Food">
                      <option value="table_booking">Table Booking & Reservations</option>
                      <option value="food_ordering">Food Ordering & Delivery</option>
                      <option value="food_inquiry">Food & Menu Inquiries</option>
                      <option value="special_offers">Special Offers & Promotions</option>
                      <option value="customer_feedback">Customer Feedback & Reviews</option>
                    </optgroup>
                    <optgroup label="Healthcare">
                      <option value="appointment_booking">Appointment Booking</option>
                      <option value="medication_reminder">Medication Reminders</option>
                      <option value="follow_up_care">Follow-up Care</option>
                      <option value="health_tips">Health Tips & Education</option>
                    </optgroup>
                    <optgroup label="E-commerce">
                      <option value="order_tracking">Order Tracking & Updates</option>
                      <option value="abandoned_cart">Abandoned Cart Recovery</option>
                      <option value="product_inquiry">Product Inquiries</option>
                      <option value="customer_support">Customer Support</option>
                    </optgroup>
                    <optgroup label="Services">
                      <option value="service_booking">Service Booking</option>
                      <option value="appointment_reminder">Appointment Reminders</option>
                      <option value="status_updates">Service Status Updates</option>
                      <option value="feedback_collection">Feedback Collection</option>
                    </optgroup>
                    <optgroup label="Custom">
                      <option value="custom_flow">Custom Business Flow</option>
                    </optgroup>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type</label>
                  <select
                    value={flowForm.trigger}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, trigger: e.target.value as any }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="appointment_created">Appointment Created</option>
                    <option value="customer_message">Customer Message</option>
                    <option value="parameter_match">Parameter Match</option>
                    <option value="ai_detected">AI Detected</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Languages</label>
                  <div className="space-y-2">
                    {['en', 'es', 'fr', 'de'].map(lang => (
                      <label key={lang} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={flowForm.languages.includes(lang)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFlowForm(prev => ({ ...prev, languages: [...prev.languages, lang] }));
                            } else {
                              setFlowForm(prev => ({ ...prev, languages: prev.languages.filter(l => l !== lang) }));
                            }
                          }}
                          className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">{lang.toUpperCase()}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateFlow}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                >
                  Create Flow
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Flow Modal */}
        {showEditForm && selectedFlow && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
              <h3 className="mb-4 text-lg font-semibold">Edit Flow</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Flow Name *</label>
                  <input
                    type="text"
                    value={flowForm.name}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter flow name"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={flowForm.description}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter flow description"
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trigger Type</label>
                  <select
                    value={flowForm.trigger}
                    onChange={(e) => setFlowForm(prev => ({ ...prev, trigger: e.target.value as any }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  >
                    <option value="appointment_created">Appointment Created</option>
                    <option value="customer_message">Customer Message</option>
                    <option value="parameter_match">Parameter Match</option>
                    <option value="ai_detected">AI Detected</option>
                    <option value="manual">Manual</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEditForm(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditFlow}
                  className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                >
                  Update Flow
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Flow Builder Modal */}
        {showFlowBuilder && selectedFlow && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-6xl max-h-[90vh] rounded-lg bg-white overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between border-b px-6 py-4 bg-gray-50">
                <h3 className="text-lg font-semibold">Flow Builder: {selectedFlow.name}</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={handleAddStep}
                    className="rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors"
                  >
                    Add Step
                  </button>
                  <button
                    onClick={handleSaveFlowSteps}
                    className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                  >
                    Save Flow
                  </button>
                  <button
                    onClick={() => setShowFlowBuilder(false)}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)] bg-gray-50">
                <div className="space-y-4">
                  {flowSteps.map((step, index) => (
                    <div key={step.id} className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Step {step.order}: {step.templateName || 'Select Template'}</h4>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setCurrentStep(step)}
                            className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-200 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setFlowSteps(prev => prev.filter(s => s.id !== step.id))}
                            className="rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-200 transition-colors"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                      
                      {step.templateId && (
                        <div className="text-sm text-gray-600 space-y-1">
                          <p><span className="font-medium">Template:</span> {step.templateName}</p>
                          <p><span className="font-medium">Language:</span> {step.language}</p>
                          <p><span className="font-medium">Delay:</span> {step.delay} minutes</p>
                          <p><span className="font-medium">Conditions:</span> {step.conditions.length}</p>
                          
                          {/* LLM Configuration Display */}
                          <div className="mt-3 p-3 bg-gray-50 rounded-md">
                            <h5 className="font-medium text-gray-800 mb-2">🤖 LLM Function Configuration</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              <div>
                                <p><span className="font-medium">Model:</span> {step.llmConfig.modelSettings.model}</p>
                                <p><span className="font-medium">Temperature:</span> {step.llmConfig.modelSettings.temperature}</p>
                                <p><span className="font-medium">Max Tokens:</span> {step.llmConfig.modelSettings.maxTokens}</p>
                              </div>
                              <div>
                                <p><span className="font-medium">Industry:</span> {step.llmConfig.businessContext.industry}</p>
                                <p><span className="font-medium">Tone:</span> {step.llmConfig.businessContext.toneOfVoice}</p>
                                <p><span className="font-medium">Required Data:</span> {step.llmConfig.functionParameters.requiredData.join(', ')}</p>
                              </div>
                            </div>
                            <div className="mt-2">
                              <p className="font-medium text-gray-700">System Prompt:</p>
                              <p className="text-xs text-gray-600 bg-white p-2 rounded border mt-1">
                                {step.llmConfig.systemPrompt.length > 100 
                                  ? step.llmConfig.systemPrompt.substring(0, 100) + '...' 
                                  : step.llmConfig.systemPrompt}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!step.templateId && (
                        <div className="text-sm text-gray-500 italic">
                          Click "Edit" to configure this step with LLM function settings
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                {flowSteps.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <div className="mb-4">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                    </div>
                    <p className="text-lg font-medium mb-2">No steps added yet</p>
                    <p className="mb-4">Click "Add Step" to start building your automated flow</p>
                    <button
                      onClick={handleAddStep}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 transition-colors"
                    >
                      Add Your First Step
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && selectedFlow && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-2xl">
              <h3 className="mb-4 text-lg font-semibold text-red-600">Delete Flow</h3>
              <p className="mb-4 text-sm text-gray-600">
                Are you sure you want to delete "{selectedFlow.name}"? This action cannot be undone.
                {selectedFlow.totalConversations > 0 && (
                  <span className="block mt-2 font-medium">
                    This will affect {selectedFlow.totalConversations} active conversations.
                  </span>
                )}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteFlow}
                  className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 transition-colors"
                >
                  Delete Flow
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step Editor Modal */}
        {showStepEditor && currentStep && (
          <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-4xl max-h-[90vh] rounded-lg bg-white overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between border-b px-6 py-4 bg-gray-50">
                <h3 className="text-lg font-semibold">Configure Step: {currentStep.templateName || 'New Step'}</h3>
                <button
                  onClick={() => setShowStepEditor(false)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Basic Step Configuration */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">📋 Basic Configuration</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                      <select
                        value={stepForm.templateId}
                        onChange={(e) => setStepForm(prev => ({ ...prev, templateId: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select a template</option>
                        {availableTemplates.map(template => (
                          <option key={template.id} value={template.id}>
                            {template.name} - {template.category}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                      <select
                        value={stepForm.language}
                        onChange={(e) => setStepForm(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Delay (minutes)</label>
                      <input
                        type="number"
                        value={stepForm.delay}
                        onChange={(e) => setStepForm(prev => ({ ...prev, delay: parseInt(e.target.value) || 0 }))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* LLM Configuration */}
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900 border-b pb-2">🤖 LLM Configuration</h4>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">AI Model</label>
                      <select
                        value={stepForm.model}
                        onChange={(e) => setStepForm(prev => ({ ...prev, model: e.target.value }))}
                        className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                      >
                        <option value="gpt-4">GPT-4 (Most Capable)</option>
                        <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Fast & Cost-effective)</option>
                        <option value="claude-3">Claude 3 (Creative & Safe)</option>
                        <option value="gemini-pro">Gemini Pro (Google's Latest)</option>
                      </select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Temperature</label>
                        <input
                          type="range"
                          value={stepForm.temperature}
                          onChange={(e) => setStepForm(prev => ({ ...prev, temperature: parseFloat(e.target.value) }))}
                          className="w-full"
                          min="0"
                          max="1"
                          step="0.1"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {stepForm.temperature} - {stepForm.temperature < 0.3 ? 'Focused' : stepForm.temperature < 0.7 ? 'Balanced' : 'Creative'}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Max Tokens</label>
                        <input
                          type="number"
                          value={stepForm.maxTokens}
                          onChange={(e) => setStepForm(prev => ({ ...prev, maxTokens: parseInt(e.target.value) || 150 }))}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                          min="50"
                          max="2000"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* System Prompt */}
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">💬 System Prompt</h4>
                  <textarea
                    value={stepForm.systemPrompt}
                    onChange={(e) => setStepForm(prev => ({ ...prev, systemPrompt: e.target.value }))}
                    placeholder="Enter the system prompt that will guide the AI's behavior for this step..."
                    rows={4}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This prompt tells the AI what role to play, what context to consider, and how to respond.
                  </p>
                </div>

                {/* Business Context */}
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">🏢 Business Context</h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                        <select
                          value={stepForm.industry}
                          onChange={(e) => setStepForm(prev => ({ ...prev, industry: e.target.value }))}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="Restaurant">Restaurant & Food</option>
                          <option value="Healthcare">Healthcare</option>
                          <option value="E-commerce">E-commerce</option>
                          <option value="Services">Professional Services</option>
                          <option value="Education">Education</option>
                          <option value="Finance">Finance</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Tone of Voice</label>
                        <select
                          value={stepForm.toneOfVoice}
                          onChange={(e) => setStepForm(prev => ({ ...prev, toneOfVoice: e.target.value }))}
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="Friendly and professional">Friendly & Professional</option>
                          <option value="Casual and conversational">Casual & Conversational</option>
                          <option value="Formal and authoritative">Formal & Authoritative</option>
                          <option value="Warm and empathetic">Warm & Empathetic</option>
                          <option value="Direct and efficient">Direct & Efficient</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 border-b pb-2 mb-4">📊 Function Parameters</h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Required Data Fields</label>
                        <input
                          type="text"
                          value={stepForm.requiredData.join(', ')}
                          onChange={(e) => setStepForm(prev => ({ 
                            ...prev, 
                            requiredData: e.target.value.split(',').map(field => field.trim()).filter(field => field)
                          }))}
                          placeholder="customer_name, order_id, booking_time"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">Comma-separated list of required data fields</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Optional Data Fields</label>
                        <input
                          type="text"
                          value={stepForm.optionalData.join(', ')}
                          onChange={(e) => setStepForm(prev => ({ 
                            ...prev, 
                            optionalData: e.target.value.split(',').map(field => field.trim()).filter(field => field)
                          }))}
                          placeholder="special_requests, delivery_notes"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">Comma-separated list of optional data fields</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="mt-8 flex justify-end space-x-3 border-t pt-6">
                  <button
                    onClick={() => setShowStepEditor(false)}
                    className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Save step configuration logic here
                      setShowStepEditor(false);
                    }}
                    className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
                  >
                    Save Step Configuration
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <LoadingComponent 
              loading={loading}
              message="Loading automated flows..."
              showRefreshButton={false}
              size="large"
            />
          </div>
        </div>
      )}
    </div>
  );
}
